import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firebaseSignOut } from './firebase';
import { api } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem('cm_token'));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken();
        localStorage.setItem('cm_token', idToken);
        setToken(idToken);
        // Verify server-side allowlist before granting access
        try {
          await api.verifyToken();
          setForbidden(false);
          setUser({
            name: firebaseUser.displayName,
            email: firebaseUser.email,
            photo: firebaseUser.photoURL,
            uid: firebaseUser.uid,
          });
        } catch (err) {
          // 403 = not on allowlist; sign them out cleanly
          localStorage.removeItem('cm_token');
          setToken(null);
          setUser(null);
          setForbidden(true);
          await firebaseSignOut();
        }
      } else {
        localStorage.removeItem('cm_token');
        setToken(null);
        setUser(null);
        setForbidden(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Refresh token periodically (Firebase tokens expire in 1 hour)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const newToken = await currentUser.getIdToken(true);
        localStorage.setItem('cm_token', newToken);
        setToken(newToken);
      }
    }, 45 * 60 * 1000); // Refresh every 45 minutes
    return () => clearInterval(interval);
  }, [user]);

  const login = (idToken, userData) => {
    localStorage.setItem('cm_token', idToken);
    setToken(idToken);
    setUser(userData);
  };

  const logout = async () => {
    await firebaseSignOut();
    localStorage.removeItem('cm_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ token, user, login, logout, isAuthenticated: !!token, loading, forbidden }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
